import { IS_SERVER } from '@app/core-features/env/env-shared';

import { logger } from '@/core/features/logger/logger';
import FeatureFlagStore from '@/core/features/store/feature-flags-store';

type NativeAppMethod =
    | 'clearCart'
    | 'indiWebViewStorageRead'
    | 'indiWebViewStorageWrite'
    | 'triggerBottomSheet'
    | 'triggerNativeNavigation'
    | 'triggerWebViewBoundariesChange'
    | 'triggerWebViewLoaded'
    | 'triggerWebViewMapClick'
    | 'triggerWebViewMapError';

const featureFlagStore = FeatureFlagStore();

export const isAppPluginEnabled = (): boolean => {
    if (IS_SERVER) {
        return false;
    }
    return !!window.webkit?.messageHandlers?.indiAppBridge || !!window.indiAppBridge;
};

export const triggerNativeNavigation = ({
    appNativeStackingDisabled,
    linkUrl,
}: {
    appNativeStackingDisabled?: true;
    linkUrl: string;
}) => {
    triggerNativeAppMethod('triggerNativeNavigation', { appNativeStackingDisabled, linkUrl });
};

export const triggerAppBottomSheet = (height: 'full' | 'half' | 'tip') => {
    triggerNativeAppMethod('triggerBottomSheet', { height });
};

export const triggerBoundariesChange = (boundaries: {
    fromLatitude: string;
    fromLongitude: string;
    toLatitude: string;
    toLongitude: string;
    zoom: string;
}) => {
    triggerNativeAppMethod('triggerWebViewBoundariesChange', { boundaries });
};

export const triggerAppWebViewLoaded = (url: string) => {
    triggerNativeAppMethod('triggerWebViewLoaded', { url });
};

export const triggerMapError = () => {
    triggerNativeAppMethod('triggerWebViewMapError', {});
};

export const triggerAppClearCart = ({
    activitySpotId,
    schoolId,
}: {
    activitySpotId: null | number | string;
    schoolId: null | number | string;
}) => {
    triggerNativeAppMethod('clearCart', { activitySpotId, schoolId });
};

export const captureNativeAppBridgeMethod =
    (methodName: string) =>
    (...args: unknown[]): void => {
        if (IS_SERVER) {
            logger.error(`Error: ${methodName} should not be called on server`);
            return;
        }
        if (document.cookie.includes('jsBridgeLogs')) {
            featureFlagStore.log(
                'jsBridgeLogs',
                `Native App Method triggered: ${methodName}, Payload: ${JSON.stringify(args)}`,
            );
        }
    };

const triggerNativeAppMethod = (method: NativeAppMethod, payload: Object): void => {
    if (IS_SERVER) {
        logger.error('Error: triggerNativeAppMethod should not be called on server');
        return;
    }

    try {
        /**
         * trigger default app bridge
         */
        if (window.C24App?.isiOSBridgeAvailable() && !!window.webkit?.messageHandlers?.appBridge) {
            window.webkit.messageHandlers.appBridge.postMessage({
                method,
                ...payload,
            });
        } else if (
            window.C24App?.isAndroidBridgeAvailable() &&
            typeof window.appBridge === 'object' &&
            window.appBridge?.hasOwnProperty(method)
        ) {
            window.appBridge[method]?.(payload);
        }

        /**
         * trigger indi app bridge
         */
        if (window.C24App?.isiOSBridgeAvailable() && !!window.webkit?.messageHandlers?.indiAppBridge) {
            window.webkit.messageHandlers.indiAppBridge.postMessage({
                method,
                ...payload,
            });
        }

        if (
            window.C24App?.isAndroidBridgeAvailable() &&
            typeof window.indiAppBridge === 'object' &&
            window.indiAppBridge.hasOwnProperty(method)
        ) {
            window.indiAppBridge[method]?.(JSON.stringify(payload));
        }
    } catch (error) {
        logger.error(`Error in triggerNativeAppMethod ${error}`);
    }
};
